import type { FormEvent } from 'react';

import type { CreateWebcastInput } from '../../../../generated/graphql-manager';

import {
  DatePicker,
  type DatePickerProps,
  Heading,
  InputDescription,
  Paragraph,
  Select,
  type SelectOption,
  SelectionInputLayout,
  TextInput,
  Toggle,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { alignedWebcastDataFromBackend } from './aligned-webcast-data';
import { DURATIONS, MINUTES, generateNextWholeHourFromNow } from './event-details.helpers';
import { eventDetailsValidation } from './event-details.validation';
import { isPassedTime } from './is-passed-time';
import {
  State,
  useCreateWebcastMutation,
  useGetLsproBrandingSettingsQuery,
  useGetWebcastQuery,
  useUpdateWebcastMutation,
} from '../../../../generated/graphql-manager';
import { getLocale } from '../../../../i18n';
import { useAbsoluteRoutes } from '../../../../routes';
import { addParamToPath, generateArrayOfNumbers } from '../../../../utils';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useCurrentUser } from '../../../hooks/current-user';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './event-details.module.css';
import managerStyles from '../../../manager.module.css';

export type WebcastData = {
  name: string;
  durationInMinutes?: string;
  startingHour?: string;
  startingMinutes?: string;
  date?: Date;
  themeId?: string | null;
  hideWebcastScheduleInViewer?: boolean;
  audioOnly?: boolean;
};

type WebcastDataToSend = Omit<CreateWebcastInput, 'ingestProtocol' | 'lsproId'>;

export type ValidationErrors = {
  name?: string;
  date?: string;
  durationInMinutes?: string;
  startingHour?: string;
  startingMinutes?: string;
  themeId?: string | null;
};

const defaultWebcast: WebcastData = {
  name: '',
  durationInMinutes: '60',
  startingHour: generateNextWholeHourFromNow().toString(),
  startingMinutes: '0',
  date: new Date(),
  themeId: '',
  audioOnly: false,
};

export function EventDetailsPage() {
  const { webcastId = '' } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const { isEventEditingAllowed } = useUserPermissions();
  const routes = useAbsoluteRoutes();
  const locale = getLocale();
  const availableHours = generateArrayOfNumbers(23);

  const webcastQueryResponse = useGetWebcastQuery({ variables: { webcastId }, skip: !webcastId });
  const webcast = webcastQueryResponse.data?.webcast;
  const archivedWebcast = webcast?.state === State.ARCHIVED;

  const brandingSettingsResponse = useGetLsproBrandingSettingsQuery({ variables: { lsproId } });
  const themes = brandingSettingsResponse.data?.lsproSettings?.themes;
  const defaultTheme = themes?.find((theme) => theme.isDefault);
  const themesSectionVisible = themes && themes.length > 1;
  const themeDoesNotExist = !themes?.find((theme) => theme.id === webcast?.themeId);

  const [webcastFormData, setWebcastFormData] = useState(
    webcast ? alignedWebcastDataFromBackend(webcast) : defaultWebcast
  );

  useEffect(() => {
    if (webcast) setWebcastFormData(alignedWebcastDataFromBackend(webcast));
  }, [webcast]);

  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const [savingFailed, setSavingFailed] = useState(false);

  const isDataReadyToBeDisplayed = webcastId !== '' && webcastFormData.name === '' && webcast === undefined;

  useEffect(() => {
    // If the theme does not exist, set the default theme
    if (themesSectionVisible && defaultTheme && (!webcast?.themeId || themeDoesNotExist)) {
      setWebcastFormData((state) => ({ ...state, themeId: defaultTheme.id }));
    }
  }, [defaultTheme, themeDoesNotExist, themesSectionVisible, webcast?.themeId]);

  const [createWebcastMutation, createWebcastMutationResponse] = useCreateWebcastMutation();

  const createWebcast = (newWebcast: WebcastDataToSend) => {
    createWebcastMutation({
      variables: { input: { ...newWebcast, lsproId } },
      onCompleted: (data) => {
        if (data.createWebcast.__typename !== 'CreateWebcastSuccess') {
          setSavingFailed(true);
          return;
        }

        setSavingFailed(false);
        navigate(addParamToPath(routes.webcastSetup_eventDetails, data.createWebcast.webcast.id));
      },
    });
  };

  const [updateWebcastMutation, updateWebcastMutationResponse] = useUpdateWebcastMutation();

  const updateWebcast = (updatedWebcast: WebcastDataToSend) => {
    if (!webcast) return;

    const updatedWebcastData = {
      id: webcastId,
      durationInMinutes: updatedWebcast.durationInMinutes,
      plannedStartAt: updatedWebcast.plannedStartAt,
      name: updatedWebcast.name,
      themeId: updatedWebcast.themeId,
      hideWebcastScheduleInViewer: updatedWebcast.hideWebcastScheduleInViewer ?? false,
    };

    updateWebcastMutation({
      variables: { input: { ...updatedWebcastData, audioOnly: updatedWebcast.audioOnly ?? false } },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            __typename: 'Webcast',
            lsproId,
            audioOnly: updatedWebcast.audioOnly ?? false,
            ...updatedWebcastData,
          },
        },
      },
      onCompleted: (data) => {
        const successFulUpdate = data.updateWebcast.__typename === 'UpdateWebcastSuccess';
        setSavingFailed(!successFulUpdate);

        if (webcastId) {
          if (successFulUpdate) {
            toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
          } else {
            notification(t('manager.webcastSetup.toast.saveError'), 'negative');
          }
        }
      },
      onError: () => {
        if (webcastId) {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        } else {
          setSavingFailed(true);
        }
      },
    });
  };

  const onWebcastDataChange = (value: string | boolean, name: string, preventSaving?: boolean) => {
    if (!preventSaving && webcastId) {
      handleWebcastFormDataChange({ ...webcastFormData, [name]: value });
    } else {
      setWebcastFormData({ ...webcastFormData, [name]: value });
    }
  };

  const onDatePickerChange = (value: DatePickerProps['value']) => {
    if (!(value instanceof Date)) return;

    const newData = { ...webcastFormData, date: value };

    if (webcastId) {
      handleWebcastFormDataChange(newData);
    }

    setWebcastFormData(newData);
    setFormErrors({ ...formErrors, date: undefined });
  };

  const handleWebcastFormDataChange = (updatedWebcastFormData: WebcastData) => {
    const validationErrors: ValidationErrors = eventDetailsValidation(updatedWebcastFormData);

    setFormErrors(validationErrors);
    setWebcastFormData(updatedWebcastFormData);

    if (Object.keys(validationErrors).length !== 0) {
      return;
    }

    if (
      updatedWebcastFormData.date &&
      updatedWebcastFormData.startingHour &&
      updatedWebcastFormData.startingMinutes &&
      updatedWebcastFormData.durationInMinutes
    ) {
      const webcastDataToSubmit: WebcastDataToSend = {
        name: updatedWebcastFormData.name,
        plannedStartAt: new Date(
          updatedWebcastFormData.date.setHours(
            Number(updatedWebcastFormData.startingHour),
            Number(updatedWebcastFormData.startingMinutes),
            0
          )
        ).toISOString(),
        durationInMinutes: Number(updatedWebcastFormData.durationInMinutes),
        themeId: themesSectionVisible ? updatedWebcastFormData.themeId : null,
        hideWebcastScheduleInViewer: updatedWebcastFormData.hideWebcastScheduleInViewer,
        audioOnly: updatedWebcastFormData.audioOnly ?? false,
      };

      if (webcastId) {
        updateWebcast(webcastDataToSubmit);
      } else {
        createWebcast(webcastDataToSubmit);
      }
    }
  };

  const handleOnNameBlur = () => {
    const nameFromBackend = webcast?.name || '';
    const newName = webcastFormData.name.trim();

    if (newName === nameFromBackend) return;

    if (webcastId) {
      handleWebcastFormDataChange(webcastFormData);
    } else {
      const validationErrors: ValidationErrors = eventDetailsValidation(webcastFormData);
      setFormErrors(validationErrors);
    }
  };

  const handleOnSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleWebcastFormDataChange(webcastFormData);
  };

  const changesLoading = createWebcastMutationResponse.loading || updateWebcastMutationResponse.loading;
  const formDisabled = !isEventEditingAllowed || archivedWebcast;
  const inPast =
    webcastFormData.date &&
    isPassedTime(
      webcastFormData.date,
      Number(webcastFormData.startingHour),
      Number(webcastFormData.startingMinutes),
      new Date()
    );

  if (isDataReadyToBeDisplayed) return null;

  return (
    <main className={managerStyles.main} data-testid="event-details-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.eventDetails.pageTitle')}</Heading>
      <Paragraph className={managerStyles.grayText}>{t('manager.webcastSetup.eventDetails.description')}</Paragraph>

      <form id="event-details-form" onSubmit={handleOnSubmit}>
        <fieldset className={styles.fieldSet} disabled={formDisabled}>
          <InputDescription
            inputId="name"
            label={t('manager.webcastSetup.eventDetails.name.label')}
            helpMessage={t('manager.webcastSetup.eventDetails.name.helpMessage')}
            disabled={archivedWebcast}
            errorMessage={formErrors.name}
          >
            <TextInput
              data-testid="name"
              placeholder={t('manager.webcastSetup.eventDetails.name.placeholder')}
              autoFocus={!webcastId}
              disabled={archivedWebcast}
              value={webcastFormData.name}
              onChange={(event) => onWebcastDataChange(event.target.value, 'name', true)}
              onBlur={handleOnNameBlur}
            />
          </InputDescription>

          <InputDescription
            inputId="date-picker"
            label={t('manager.webcastSetup.eventDetails.date.label')}
            disabled={formDisabled}
            errorMessage={formErrors.date}
            helpMessage={inPast ? t('manager.webcastSetup.eventDetails.time.validation.passed') : undefined}
          >
            <DatePicker
              disablePastDays={false}
              value={webcastFormData.date}
              onChange={onDatePickerChange}
              showCalendarIcon
              locale={locale}
              disabled={formDisabled}
            />
          </InputDescription>

          <div className={styles.dropdowns}>
            <InputDescription
              inputId="starting-hour"
              label={t('manager.webcastSetup.eventDetails.time.hour.label')}
              disabled={formDisabled}
              errorMessage={formErrors.startingHour}
            >
              <Select
                data-testid="starting-hour"
                placeholder={t('manager.webcastSetup.eventDetails.time.hour.placeholder')}
                disabled={formDisabled}
                optionsIdProperty="key"
                value={{ key: webcastFormData.startingHour }}
                options={availableHours.map((hour) => ({ key: String(hour) }))}
                onChange={(option: SelectOption) => onWebcastDataChange(option.key, 'startingHour')}
              />
            </InputDescription>

            <InputDescription
              inputId="starting-minutes"
              label={t('manager.webcastSetup.eventDetails.time.minutes.label')}
              disabled={formDisabled}
              errorMessage={formErrors.startingMinutes}
            >
              <Select
                data-testid="starting-minutes"
                placeholder={t('manager.webcastSetup.eventDetails.time.minutes.placeholder')}
                disabled={formDisabled}
                optionsIdProperty="key"
                value={{
                  key: webcastFormData.startingMinutes,
                  label: MINUTES.find(({ value }) => value === webcastFormData.startingMinutes)?.label,
                }}
                options={MINUTES.map(({ value, label }) => ({ key: value, label }))}
                onChange={(option: SelectOption) => onWebcastDataChange(option.key, 'startingMinutes')}
              />
            </InputDescription>

            <InputDescription
              inputId="duration-in-minutes"
              label={t('manager.webcastSetup.eventDetails.duration.placeholder')}
              disabled={formDisabled}
              errorMessage={formErrors.durationInMinutes}
            >
              <Select
                data-testid="duration-in-minutes"
                placeholder={t('manager.webcastSetup.eventDetails.duration.placeholder')}
                disabled={formDisabled}
                optionsIdProperty="key"
                value={{
                  key: webcastFormData.durationInMinutes,
                  label: DURATIONS(t).find(({ value }) => value === Number(webcastFormData.durationInMinutes))?.label,
                }}
                options={DURATIONS(t).map(({ value, label }) => ({ key: String(value), label }))}
                onChange={(option: SelectOption) => onWebcastDataChange(option.key, 'durationInMinutes')}
              />
            </InputDescription>
          </div>

          <InputDescription label={t('manager.webcastSetup.eventDetails.eventTime.label')} style={{ marginTop: 40 }}>
            <SelectionInputLayout
              variant="ghost"
              checked={Boolean(webcastFormData.hideWebcastScheduleInViewer)}
              disabled={formDisabled}
              inputElement={
                <Toggle
                  data-testid="hide-webcast-schedule-in-viewer-toggle"
                  disabled={formDisabled}
                  checked={Boolean(webcastFormData.hideWebcastScheduleInViewer)}
                  onChange={() =>
                    onWebcastDataChange(!webcastFormData.hideWebcastScheduleInViewer, 'hideWebcastScheduleInViewer')
                  }
                >
                  {t('manager.webcastSetup.eventDetails.eventTime.toggle')}
                </Toggle>
              }
            />
          </InputDescription>

          {themesSectionVisible && (
            <InputDescription label={t('manager.webcastSetup.eventDetails.theme.label')}>
              <Select
                data-testid="theme-select"
                optionsIdProperty="key"
                value={{
                  key: webcastFormData.themeId,
                  label: themes.find((theme) => theme.id === webcastFormData.themeId)?.name,
                }}
                options={themes.map((theme) => ({ key: theme.id, label: theme.name }))}
                onChange={(option: SelectOption) => onWebcastDataChange(option.key, 'themeId')}
              />
            </InputDescription>
          )}

          <InputDescription label={t('manager.webcastSetup.eventDetails.eventType.label')}>
            <Select
              data-testid="event-type-select"
              optionsIdProperty="key"
              value={{
                key: webcastFormData.audioOnly ? 'audio-only' : 'video',
                label: webcastFormData.audioOnly
                  ? t('manager.webcastSetup.eventDetails.eventType.audioOnly')
                  : t('manager.webcastSetup.eventDetails.eventType.video'),
              }}
              options={[
                { key: 'video', label: t('manager.webcastSetup.eventDetails.eventType.video') },
                { key: 'audio-only', label: t('manager.webcastSetup.eventDetails.eventType.audioOnly') },
              ]}
              onChange={(option: SelectOption) => onWebcastDataChange(option.key === 'audio-only', 'audioOnly')}
            />
          </InputDescription>
        </fieldset>
      </form>

      <SetupPageFooter
        nextLabel={t('manager.webcastSetup.eventDetails.nextStep.label')}
        route={routes.webcastSetup_eventPermissions}
        showSaveButton={!webcastId}
        nextButtonDisabled={!webcast}
        saveButtonDisabled={formDisabled}
        saving={changesLoading}
        savingFailed={savingFailed}
        saveButtonLabel={t('manager.webcastSetup.createButton')}
      />
    </main>
  );
}
